<template>
  <svg :x="x" :y="y" :width="`${matOuterWidth}in`" :height="`${matOuterHeight}in`" xmlns="http://www.w3.org/2000/svg" :index="matIndex" :key="matKey">
    <defs>
      <mask :id="`${uuid}-mat-mask-${matIndex}`">
        <rect width="100%" height="100%" fill="white"/>
        <rect
          v-for="artworkOpening in artworkOpenings"
          :key="artworkOpening.key"
          :x="artworkOpening.x"
          :y="artworkOpening.y"
          :width="artworkOpening.width"
          :height="artworkOpening.height"
          fill="black"
        />
      </mask>

      <pattern
        v-if="fullMat.surfaceTextureUrl"
        :x="surfaceTexturePatternOrigin.x"
        :y="surfaceTexturePatternOrigin.y"
        :id="`${uuid}-mat-tile-pattern-${matIndex}`"
        patternUnits="userSpaceOnUse"
        :width="fullMat.surfaceTextureWidthInInches"
        :height="fullMat.surfaceTextureHeightInInches"
      >
        <image
          :href="fullMat.surfaceTextureUrl"
          :width="fullMat.surfaceTextureWidthInInches"
          :height="fullMat.surfaceTextureHeightInInches"
          preserveAspectRatio="xMaxYMin"
        />
      </pattern>

      <linearGradient
        v-if="colorBlockColors"
        :id="`${uuid}-mat-color-block-${matIndex}`"
        x1="0" y1="0" x2="0" y2="1"
      >
        <stop offset="0%" :stop-color="colorBlockColors[0]" />
        <stop offset="50%" :stop-color="colorBlockColors[0]" />
        <stop offset="50%" :stop-color="colorBlockColors[1]" />
        <stop offset="100%" :stop-color="colorBlockColors[1]" />
      </linearGradient>
    </defs>
    <rect
      :width="matOuterWidth"
      :height="matOuterHeight"
      :fill="matColor"
      :mask="`url(#${uuid}-mat-mask-${matIndex})`"
    />
    <image v-if="matImage" :href="matImage" :width="matOuterWidth" :height="matOuterHeight"/>
    <template v-else>
      <rect
        v-for="artworkOpening in artworkOpenings"
        :key="artworkOpening.key"
        :x="artworkOpening.bevelX"
        :y="artworkOpening.bevelY"
        :width="artworkOpening.bevelWidth"
        :height="artworkOpening.bevelHeight"
        :stroke="coreColor"
        :stroke-width="bevelWidth"
        fill="none"
      />
    </template>
    <svg
      v-for="openingIcon in templateOpeningIcons"
      :key="`opening-icon-${openingIcon.key}`"
      class="template-opening-icon"
      xmlns="http://www.w3.org/2000/svg"
      :x="openingIcon.x"
      :y="openingIcon.y"
      :width="openingIcon.size"
      :height="openingIcon.size"
      viewBox="0 0 48 48"
      preserveAspectRatio="xMidYMid meet"
    >
      <path d="M43,8H5A.9.9,0,0,0,4,9V39a.9.9,0,0,0,1,1H43a.9.9,0,0,0,1-1V9A.9.9,0,0,0,43,8ZM42,38H6V10H42Z"/>
      <path d="M15,18a6,6,0,0,0,6-6,1,1,0,0,0-2,0,4,4,0,0,1-8,0,.9.9,0,0,0-1-1,.9.9,0,0,0-1,1A6,6,0,0,0,15,18Z"/>
      <path d="M9,34a.9.9,0,0,0,.7-.3L15,28.4l4.3,4.3a1,1,0,0,0,1.4,0L31,22.4l7.3,7.3a1,1,0,1,0,1.4-1.4l-8-8a1,1,0,0,0-1.4,0L20,30.6l-4.3-4.3a1,1,0,0,0-1.4,0l-6,6a1,1,0,0,0,0,1.4A.9.9,0,0,0,9,34Z"/>
    </svg>
    <RenderSvgMatCaption
      v-if="matCaption"
      :frameSpec="frameSpec"
      :captionX="captionX"
      :captionY="captionY"
      :captionWidth="captionWidth"
    />
    <RenderSvgMatDecoration
      v-if="matIndex === frameSpec.mats.length - 1"
      :frameSpec="frameSpec"
      :artworkOpenings="artworkOpenings"
      :matWidth="matOuterWidth"
      :matHeight="matOuterHeight"
      :matMaskId="`${uuid}-mat-mask-${matIndex}`"
    />
    <rect
      v-if="hasPlaque"
      :stroke="coreColor"
      :stroke-width="bevelWidth"
      :x="plaqueX"
      :y="plaqueY"
      :width="plaqueWidth"
      :height="plaqueHeight"
      fill="none"
    />
    <RenderSvgPlaque
      v-if="hasPlaque"
      :frameSpec="frameSpec"
      :plaqueY="plaqueY + bevelWidth/2"
      :plaqueX="plaqueX + bevelWidth/2"
      :plaqueWidth="plaqueWidth - bevelWidth"
      :plaqueHeight="plaqueHeight - bevelWidth"
    />
  </svg>
</template>

<script>
import uuid from 'uuid/v4';
import { defaultMatThickness, uploadedArtworkMatOverlap, getMatCaptionOffsetDown, colorBlockMatColors } from "../../hardCodedValues";
import RenderSvgMatCaption from './RenderSvgMatCaption.vue';
import RenderSvgMatDecoration from './RenderSvgMatDecoration.vue';
import RenderSvgPlaque from './RenderSvgPlaque.vue';

const templateOpeningIconRatio = 0.4;

export default {
  components: {
    RenderSvgMatCaption,
    RenderSvgMatDecoration,
    RenderSvgPlaque
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    mat: {
      type: Object,
      required: true
    },
    matIndex: {
      type: Number,
      required: true
    },
    templatePreview: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      uuid: uuid()
    };
  },
  computed: {
    fullMat () {
      return this.frameSpec.getFullMat(this.mat.name);
    },
    matTemplate () {
      return this.frameSpec.matTemplate;
    },
    openings () {
      return this.matTemplate?.openings || this.fullMat?.openings || [];
    },
    matKey() {
      return `${this.mat.name}-${this.matIndex}`;
    },
    openingGeometry () {
      const geometry = this.frameSpec.matOpeningGeometry();

      return geometry[geometry.length - 1 - this.matIndex];
    },
    matImage () {
      if (this.fullMat && this.fullMat.matAsset) {
        // Mat templates (duets, trios, etc) render via SVG
        // using surfaceColor/surfaceTextureUrl + mask cutouts + bevels, so skip
        // the image overlay.
        // We will want to move off the mat asset for heartstagram (and potentially other shapes) next
        if (this.hasMultipleOpenings) {
          return null;
        }
        return this.fullMat.matAsset;
      }
      return null;
    },
    colorBlockColors() {
      return colorBlockMatColors[this.fullMat?.name] ?? null;
    },
    matColor() {
      if (!this.fullMat) {
        return 'white';
      } else if (this.colorBlockColors) {
        return `url(#${this.uuid}-mat-color-block-${this.matIndex})`;
      } else if (this.fullMat.surfaceTextureUrl) {
        return `url(#${this.uuid}-mat-tile-pattern-${this.matIndex})`;
      } else {
        return this.fullMat.surfaceColor;
      }
    },
    surfaceTexturePatternOrigin () {
      if (!this.fullMat.surfaceTextureWidthInInches || !this.fullMat.surfaceTextureHeightInInches) {
        return {
          x: '0',
          y: '0'
        };
      }

      return {
        x: (this.matOuterWidth - this.fullMat.surfaceTextureWidthInInches) / 2,
        y: (this.matOuterHeight - this.fullMat.surfaceTextureHeightInInches) / 2
      };
    },
    coreColor() {
      return this.fullMat ? this.fullMat.coreColors[0] : 'black';
    },
    bevelWidth() {
      return defaultMatThickness; // 1/16th of an inch
    },
    matOuterWidth() {
      if (this.fullMat && this.fullMat.matWidth) {
        return this.fullMat.matWidth;
      }

      return this.frameSpec.matOuterSize().width || 0; // In inches
    },
    matOuterHeight() {
      if (this.fullMat && this.fullMat.matHeight) {
        return this.fullMat.matHeight;
      }

      return this.frameSpec.matOuterSize().height || 0; // In inches
    },
    matOverlapInset() {
      return this.frameSpec.getFullPrimaryMat()?.matAsset || this.fullMat?.openings?.length > 1
        ? uploadedArtworkMatOverlap / 2
        : 0;
    },
    firstArtworkOpeningX() {
      return this.openingGeometry.inset.leftInInches + this.matOverlapInset;
    },
    firstArtworkOpeningY() {
      return this.openingGeometry.inset.topInInches + this.matOverlapInset;
    },
    hasMultipleOpenings() {
      return this.openings.length > 1;
    },
    artworkOpenings() {
      // multi-opening mats - use the template's openings to calculate the openings
      // there's 1 artwork so we can't use artwork dims here
      if (this.hasMultipleOpenings) {
        return this.openings.map((opening, index) => {
          const x = this.firstArtworkOpeningX + opening.leftInInches;
          const y = this.firstArtworkOpeningY + opening.topInInches;
          const width = opening.widthInInches;
          const height = opening.heightInInches;

          return {
            key: index,
            x,
            y,
            width,
            height,
            bevelX: x - (this.bevelWidth / 2),
            bevelY: y - (this.bevelWidth / 2),
            bevelWidth: width + this.bevelWidth,
            bevelHeight: height + this.bevelWidth,
          };
        });
      }

      return this.frameSpec.artworks.map((artwork, artworkKey) => {
        // This defines the rectangular cutout mask where artwork is visible through the mat.
        // Mat images with shaped opening (heartstagram), the image itself handles the opening, so the visible portion should be the whole mat.
        if (this.matImage) {
          return {
            key: artworkKey,
            x: 0,
            y: 0,
            width: this.matOuterWidth,
            height: this.matOuterHeight,
            bevelX: 0,
            bevelY: 0,
            bevelWidth: this.matOuterWidth,
            bevelHeight: this.matOuterHeight,
          };
        }

        const x = this.firstArtworkOpeningX;
        const y = this.firstArtworkOpeningY;
        const width = !artwork ? 0 : this.openingGeometry.widthInInches;
        const height = !artwork ? 0 : this.openingGeometry.heightInInches;

        return {
          key: artworkKey,
          x,
          y,
          width,
          height,
          bevelX: x - (this.bevelWidth / 2),
          bevelY: y - (this.bevelWidth / 2),
          bevelWidth: width + this.bevelWidth,
          bevelHeight: height + this.bevelWidth,
        };
      });
    },
    templateOpeningIcons() {
      if (!this.templatePreview || !this.matTemplate) return [];

      return this.artworkOpenings.map((artworkOpening) => {
        const size = Math.min(artworkOpening.width, artworkOpening.height) * templateOpeningIconRatio;

        return {
          key: artworkOpening.key,
          size,
          x: artworkOpening.x + ((artworkOpening.width - size) / 2),
          y: artworkOpening.y + ((artworkOpening.height - size) / 2)
        };
      });
    },
    matCaption() {
      return this.mat.matCaption || null;
    },
    captionX() {
      return this.firstArtworkOpeningX;
    },
    captionY() {
      const bottomReveal = this.openingGeometry.inset.bottomInInches;
      const offsetDown = getMatCaptionOffsetDown(bottomReveal);
      return this.matOuterHeight - bottomReveal + offsetDown;
    },
    captionWidth() {
      const reveal = this.frameSpec.getReveal() || {};
      const inset = this.matImage || this.hasMultipleOpenings ? uploadedArtworkMatOverlap : 0;

      return this.matOuterWidth
        - (reveal.leftInInches || 0)
        - (reveal.rightInInches || 0)
        - inset;
    },
    hasPlaque() {
      // todo: ensure this checks the actual value on the frameSpec
      return this.frameSpec.matPlaque || this.frameSpec.isAutoMatPlaque();
    },
    plaqueWidth() {
      // note: these are not being handled like a true artwork opening/overlap.
      // for now this number represents the plaque opening for rendering purposes. if/when we move to custom with a true second opening, we can adjust.
      // eventually we will have small vs large sizes as well
      const smallPlaqueWidth = 3.5;  // Fixed width for small plaque
      return smallPlaqueWidth;
    },
    plaqueHeight() {
      // note: these are not being handled like a true artwork opening/overlap.
      // for now this number represents the plaque opening for rendering purposes. if/when we move to custom with a true second opening, we can adjust.
      // eventually we will have small vs large sizes as well
      const smallPlaqueHeight = 2.0;  // Fixed height for small plaque
      return smallPlaqueHeight;
    },
    plaqueY() {
      const sizeGroup = this.frameSpec.sizeGroup();

      // we were supposed to have different distances for small vs large plaque, it looks like
      // the first products are setting the distance based on the art size

      const smallPlaqueOffset = 0.75; // 0.75 inches below the artwork opening
      const largePlaqueOffset = 1; // 1 inch below the artwork opening

      let plaqueOffset = smallPlaqueOffset;
      if (sizeGroup && !["XS", "Mini"].includes(sizeGroup.name)) {
        plaqueOffset = largePlaqueOffset;
      }

      return this.matOuterHeight - this.openingGeometry.inset.bottomInInches + plaqueOffset + (this.bevelWidth / 2);
    },
    plaqueX() {
      // from the left opening, subtract half of the opening, and half of the plaque width to center
      const baseMatOpeningWidth = this.captionWidth;
      return this.firstArtworkOpeningX + (baseMatOpeningWidth / 2) - (this.plaqueWidth / 2);
    }
  }
};
</script>

<style lang="scss" scoped>
svg {
  align-items: center;
  display: flex;
  height: auto;
  justify-content: center;
  max-width: 100%;
  pointer-events: none;
  width: 100%;

  rect {
    pointer-events: none;
  }

  .template-opening-icon path {
    fill: #adadad;
  }
}
</style>
